/* eslint-disable no-console */
import * as ActionTypes from 'actions/ActionTypes'
import * as xmlFormat from 'xml-formatter'
import { flatten } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { adjustProjectReference } from 'actions/projectData/adjustProjectReference'
import { createEditingTask } from '~/ServerAPI'
import { createEditingTaskShotStack } from '~/ShotStackServerAPI'
import { createMediaFile } from '../sourceFiles'
import { FILE_TYPE, RENDER_BACKEND, TASK_STATUS } from '~/enums'
import * as Selectors from '~/selectors'
import { selectActiveProjectId, selectProjectForSaving } from '~/selectors/projectData'
import { checkStatusLongPolling, updateEditingTaskStatus } from './checkStatusLongPolling'
import createEditingXTL from './lib'
import { createShotStackTimeline } from './lib/createShotStackTimeline'
import { uploadOverlays } from './overlayAssets'
import { selectorPreviewTimelineIOPoints } from '~/selectors/preview'
import { buildFlatTimeline } from '~/lib'
import { getAssetsByIOPoints } from '~/helpers/assets/getAssetsByIOPoints'
import { selectRenderSettings, selectBehaviourSettings } from '~/selectors/mainView'
import { createImages } from './lib/createImages'
import { getChunkArray } from '~/Util/arrays'
import { yieldToBrowser } from '~/Util'
import { deleteTask } from './deleteTask'
import { selectImageOverlayMaxSize } from '~/selectors/app'

// ---

export const addEditingTask = ({ task, loading = false }) => ({
  type: ActionTypes.ADD_EDITING_TASK,
  payload: { task },
  meta: { loading },
})

export const addEditingTaskError = ({ err }) => ({
  type: ActionTypes.ADD_EDITING_TASK,
  payload: { err },
  error: true,
})

export const resetEditingTaskError = () => ({
  type: ActionTypes.RESET_EDITING_TASK_ERROR,
  payload: {},
})

export const setSelectedTask = id => ({
  type: ActionTypes.SET_SELECTED_TASK,
  payload: { id },
})

export const prepareTask = (name, path = '') => ({
  type: ActionTypes.PREPARE_EDITING_TASK,
  payload: { name, path },
})

export const setAbsFilePath = (path = '') => ({
  type: ActionTypes.SET_ABS_FILE_PATH,
  payload: { path },
})

export const requestCreateTask = (outputName, format,
  abortController) => async (dispatch, getState) => {
  dispatch(addEditingTask({ loading: true }))
  dispatch(adjustProjectReference())

  try {
    const state = getState()
    const activeProjectId = selectActiveProjectId(state)
    const previewParams = Selectors.selectPreviewParams(state)
    const visibleLayerIds = Selectors.getVisibleLayerIds(state)
    const unmutedLayerIds = Selectors.getUnmutedLayerIds(state)
    const refVideo = Selectors.getReferenceVideoAsset(state)
    const imageOverlayMaxSize = selectImageOverlayMaxSize(state)
    const { markIn, markOut } = selectorPreviewTimelineIOPoints(state)
    const { scale, boundingRect } = previewParams
    const overlayAssets = Selectors.selectExistingOverlayAssets(state)
    const visibleAssets = overlayAssets
      .filter(asset => visibleLayerIds.includes(asset.layerId))

    // necessary to see the loader before downloading files
    await yieldToBrowser()
    let preparedImages = []
    const prepareImageTaskId = uuidv4()

    if (visibleAssets.length) {
      const responseOverlayImages = []
      const preparedImageTask = {
        id: prepareImageTaskId,
        error: '',
        status: TASK_STATUS.PREPARE_IMAGES,
        name: `${outputName}.${format}`,
        projectId: activeProjectId,
        fileId: null,
        progress: 0,
        total: 0,
        count: 0,
        outputUrls: [],
      }
      dispatch(addEditingTask({ task: preparedImageTask }))

      const BATCH_SIZE = 4
      let prepareImagesNum = 0
      const batches = getChunkArray(visibleAssets, BATCH_SIZE)
      for (const batch of batches) {
        // Upload aborted before starting new batch
        if (abortController?.signal.aborted) {
          break
        }
        const { imagePromises, preparePositionsXY, assetBlobs } = createImages(batch,
          refVideo, abortController, { scale, boundingRect, imageOverlayMaxSize })

        // eslint-disable-next-line no-await-in-loop
        await Promise.all(imagePromises)

        if (abortController?.signal.aborted) {
          break
        }

        // eslint-disable-next-line no-await-in-loop
        const batchResults = await dispatch(uploadOverlays(assetBlobs, abortController))

        for (let i = 0; i < batch.length; i++) {
          if (abortController?.signal.aborted) {
            break
          }

          // eslint-disable-next-line
          const resultOverlay = await batchResults[i]

          if (resultOverlay?.error) {
            throw resultOverlay.error
          }

          preparedImages.push(preparePositionsXY[i])
          responseOverlayImages.push(resultOverlay)

          prepareImagesNum += 1
          if (abortController?.signal.aborted) {
            break
          }
          const status = {
            id: prepareImageTaskId,
            count: prepareImagesNum,
            total: visibleAssets.length,
            progress: Math.ceil((prepareImagesNum / visibleAssets.length) * 100),
          }
          dispatch(updateEditingTaskStatus(status))
        }
        if (abortController?.signal.aborted) {
          break
        }
      }

      if (abortController?.signal.aborted) {
        dispatch(deleteTask(prepareImageTaskId, false))
        return
      }

      preparedImages = responseOverlayImages
        .filter(Boolean)
        .map(({ assetId, data }) => ({
          ...preparedImages.find(({ assetId: aId }) => aId === assetId),
          ...data,
        }))
    }
    let taskId = null
    let fileId = null
    if (__CFG__.RENDER_BACKEND === RENDER_BACKEND.SOLVEIG) {
      const state = getState()
      const {
        existAssets, layers, previewParams, refAsset,
      } = Selectors.getBuildFlatTimelineData(state)
      const { skipEmptySpace } = selectRenderSettings(state)
      const { ignoreQuantizerVideoParamsMismatch } = selectBehaviourSettings(state)
      const ioPointsAssets = getAssetsByIOPoints(
        existAssets,
        refAsset,
        { skipEmptySpace: Boolean(skipEmptySpace), generateNewId: false, markIn, markOut }
      )
      const assets = flatten(ioPointsAssets)
      const assetsGroups = buildFlatTimeline(ioPointsAssets, layers, previewParams, refVideo)
      const layersIndexes = Selectors.getLayersIndexes(getState())
      const editingXTL = xmlFormat(
        createEditingXTL(
          outputName,
          format,
          assetsGroups,
          assets,
          layersIndexes,
          preparedImages,
          previewParams,
          refVideo,
          unmutedLayerIds,
          Boolean(skipEmptySpace),
          Boolean(ignoreQuantizerVideoParamsMismatch)
        )
      )
      console.log(editingXTL)
      const response = await createEditingTask(editingXTL, activeProjectId)
      const { error } = response.data
      fileId = response.data.fileId
      taskId = response.data.taskId
      if (error) {
        throw Error(error)
      }
    } else if (__CFG__.RENDER_BACKEND === RENDER_BACKEND.SHOTSTACK) {
      const project = selectProjectForSaving(state)
      const timeline = createShotStackTimeline(project, refVideo, preparedImages)
      const response = await createEditingTaskShotStack(timeline)
      taskId = response.data.response.id
    }
    const task = {
      id: taskId,
      name: `${outputName}.${format}`,
      projectId: activeProjectId,
      status: TASK_STATUS.NEW,
      fileId,
      progress: 0,
      error: '',
      outputUrls: [],
    }
    if (__CFG__.SAVE_SETTINGS.CREATE_OUTPUT_MEDIA) {
      dispatch(createMediaFile(fileId, FILE_TYPE.OUTPUT))
    }
    dispatch(addEditingTask({ task }))
    dispatch(deleteTask(prepareImageTaskId))
    setTimeout(checkStatusLongPolling, 0, dispatch, taskId)
  } catch (error) {
    const msg = error.message
      || error.data?.error
      || error.data?.message
      || error.statusText
      || error.toString()
    const err = new Error(msg)
    dispatch(addEditingTaskError({ err }))
  }
}
